import { useEffect, useRef } from 'react';

/**
 * Subscribe to the global `iftmx:reconnected` event so a component
 * can refresh its data when the network comes back, without having
 * to close and reopen the app.
 *
 * The event is dispatched by `useOnlineStatus` whenever the browser
 * transitions from offline → online.
 *
 * Usage:
 *   useReconnectRefetch(() => {
 *     fetchSomething();
 *   });
 *
 * The callback can be inline (it's stored in a ref so re-creating it
 * on every render does NOT cause the listener to detach/reattach).
 */
export function useReconnectRefetch(callback: () => void) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const handler = () => cbRef.current();
    window.addEventListener('iftmx:reconnected', handler);
    return () => window.removeEventListener('iftmx:reconnected', handler);
  }, []);
}
